import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Calculator } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { SERVICES, PROMOS } from "../data/siteData";

const SIZES = [
  { id: "townhouse", label: "Townhouse / small lot", mult: 1 },
  { id: "standard", label: "Standard lot (~4,000 sq ft)", mult: 1.4 },
  { id: "estate", label: "Large estate (8,000+ sq ft)", mult: 2 },
  { id: "commercial", label: "Commercial acreage", mult: 3 },
];

function Estimator() {
  const [size, setSize] = useState("standard");
  const [picked, setPicked] = useState(["grass-cutting"]);
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  const mult = SIZES.find((s) => s.id === size).mult;
  const estimable = SERVICES.filter((s) => s.estimate);
  const promo = PROMOS.find((p) => p.code === code.trim().toUpperCase());

  const [low, high] = useMemo(() => {
    let lo = 0;
    let hi = 0;
    picked.forEach((id) => {
      const s = SERVICES.find((x) => x.id === id);
      if (s?.estimate) {
        lo += s.estimate[0];
        hi += s.estimate[1];
      }
    });
    lo = Math.round(lo * mult);
    hi = Math.round(hi * mult);
    if (promo) {
      lo = Math.max(0, lo - promo.value);
      hi = Math.max(0, hi - promo.value);
    }
    return [lo, hi];
  }, [picked, mult, promo]);

  const toggle = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const transfer = () => {
    const names = picked.map((id) => SERVICES.find((s) => s.id === id)?.title).filter(Boolean).join(", ");
    const sizeLabel = SIZES.find((s) => s.id === size).label;
    navigate("/contact", {
      state: {
        service: names || "General enquiry",
        message: `Estimate request — Property: ${sizeLabel}. Services: ${names || "none selected"}. Ballpark: $${low}–$${high}${promo ? ` (promo ${promo.code} applied)` : ""}. Please confirm an exact quote.`,
      },
    });
  };

  return (
    <div
      className="border border-[#234E33]/12 bg-[#1A2E20] p-8 text-[#FBF9F5] lg:p-10"
      data-testid="estimate-calculator-container"
    >
      <div className="flex items-center gap-3">
        <Calculator size={20} className="text-[#72B043]" />
        <h3 className="font-display text-2xl font-bold">Instant Estimate</h3>
      </div>
      <p className="mt-2 text-sm text-[#FBF9F5]/60">
        A ballpark in ten seconds. Hardscaping, sodding, gardens and snow are quoted on-site.
      </p>

      <p className="mt-7 font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#72B043]">Property size</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SIZES.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`estimator-size-${s.id}`}
            onClick={() => setSize(s.id)}
            className={`border px-4 py-3 text-left text-sm transition-colors duration-200 ${
              size === s.id
                ? "border-[#72B043] bg-[#72B043]/10 text-[#FBF9F5]"
                : "border-[#FBF9F5]/15 text-[#FBF9F5]/60 hover:border-[#FBF9F5]/40"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <p className="mt-7 font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#72B043]">Services</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {estimable.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`estimator-service-${s.id}`}
            onClick={() => toggle(s.id)}
            className={`flex items-center justify-between border px-4 py-3 text-left text-sm transition-colors duration-200 ${
              picked.includes(s.id)
                ? "border-[#72B043] bg-[#72B043]/10 text-[#FBF9F5]"
                : "border-[#FBF9F5]/15 text-[#FBF9F5]/60 hover:border-[#FBF9F5]/40"
            }`}
          >
            {s.title}
            {picked.includes(s.id) && <Check size={15} className="shrink-0 text-[#72B043]" />}
          </button>
        ))}
      </div>

      <p className="mt-7 font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#72B043]">Promo code (optional)</p>
      <input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="SKLOC-SPRING15"
        data-testid="estimator-promo-input"
        className="mt-3 w-full border border-[#FBF9F5]/15 bg-transparent px-4 py-3 text-sm text-[#FBF9F5] placeholder-[#FBF9F5]/30 outline-none focus:border-[#72B043]"
      />
      {code.trim() && (
        <p className="mt-2 text-xs text-[#FBF9F5]/60" data-testid="estimator-promo-status">
          {promo ? `${promo.headline} applied.` : "Code not recognized — check the spelling."}
        </p>
      )}

      <div className="mt-8 flex flex-wrap items-end justify-between gap-6 border-t border-[#FBF9F5]/15 pt-6">
        <div>
          <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#72B043]">Ballpark</p>
          <p className="mt-1 font-display text-4xl font-black" data-testid="estimator-result">
            {picked.length ? `$${low} – $${high}` : "—"}
          </p>
        </div>
        <button
          type="button"
          onClick={transfer}
          disabled={!picked.length}
          data-testid="calculator-transfer-to-quote-btn"
          className="group inline-flex items-center gap-2 rounded-full bg-[#72B043] px-6 py-3.5 text-sm font-bold text-[#0C0E0D] transition-colors duration-200 hover:bg-[#FBF9F5] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Send as Enquiry
          <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
        </button>
      </div>
    </div>
  );
}

export default function Services() {
  return (
    <main className="bg-[#FBF9F5] pt-28 lg:pt-36">
      <section className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow>Services & Rates</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black tracking-tight text-[#1A2E20] sm:text-5xl lg:text-6xl">
            Nine crafts. One crew.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4a544c] sm:text-lg">
            From weekly cuts to full stonework builds, every service is quoted plainly and finished like it's our own
            front yard.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <article
                data-testid={`service-card-${s.id}`}
                className="group flex h-full flex-col border border-[#234E33]/12 bg-[#FAF7F2] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C86D51]">{s.type}</p>
                  <h2 className="mt-2 font-display text-xl font-bold text-[#1A2E20]">{s.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-[#4a544c]">{s.description}</p>
                  <ul className="mt-4 space-y-2">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-[#1A2E20]/80">
                        <Check size={13} className="mt-0.5 shrink-0 text-[#2E6941]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-5">
                    <p className="text-sm font-bold text-[#2E6941]">{s.pricing}</p>
                    <p className="mt-0.5 text-xs text-[#4a544c]">{s.pricingNote}</p>
                    <Link
                      to="/contact"
                      state={{ service: s.title }}
                      data-testid={`service-quote-${s.id}`}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#1A2E20] transition-colors hover:text-[#2E6941]"
                    >
                      Request this service <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <Reveal>
          <Estimator />
        </Reveal>
      </section>
    </main>
  );
}
